<template>
  <div class="stock-order-schedules">
    <div class="d-flex align-center mb-1">
      <span class="caption grey--text">{{ $t("STOCK.SALES_ORDERS.SCHEDULES") }}</span>
      <v-spacer />
      <v-btn v-if="canEdit && !allAssigned" x-small text color="primary" @click="addSchedule">
        {{ $t("STOCK.SALES_ORDERS.SPLIT_SCHEDULE") }}
      </v-btn>
    </div>
    <div v-if="onlyUnplanned && !visibleSchedules.length" class="caption grey--text">
      {{ $t("STOCK.SALES_ORDERS.ALL_SCHEDULES_ON_DN") }}
    </div>
    <v-simple-table v-else dense>
      <thead>
        <tr>
          <th style="width: 18%">{{ $t("STOCK.SALES_ORDERS.QTY") }}</th>
          <th style="width: 18%">{{ $t("STOCK.DELIVERY_NOTES.ETD") }}</th>
          <th style="width: 18%">{{ $t("STOCK.DELIVERY_NOTES.ETA") }}</th>
          <th v-if="showDestination" style="width: 18%">{{ $t("STOCK.PURCHASE_ORDERS.SCHEDULE_DESTINATION") }}</th>
          <th v-if="showSource" style="width: 18%">{{ $t("STOCK.SALES_ORDERS.SCHEDULE_SOURCE") }}</th>
          <th class="text-center" style="width: 56px">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon small v-bind="attrs" v-on="on">mdi-note-text-outline</v-icon>
              </template>
              <span>{{ $t("STOCK.SALES_ORDERS.SCHEDULE_NOTE") }}</span>
            </v-tooltip>
          </th>
          <th style="width: 24%">{{ $t("STOCK.DELIVERY_NOTES.DOCUMENT_CODE") }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="entry in visibleSchedules" :key="entry.schedule.id || `new-${entry.index}`">
          <td>
            <v-text-field
              v-model.number="entry.schedule.quantity"
              type="number"
              solo
              dense
              hide-details
              :disabled="!canEditSchedule(entry.schedule)"
            />
          </td>
          <td>
            <DatePicker
              v-model="entry.schedule.etd_date"
              :disabled="!canEditSchedule(entry.schedule)"
              without-hint
              clearable
            />
          </td>
          <td>
            <DatePicker
              v-model="entry.schedule.eta_date"
              :disabled="!canEditSchedule(entry.schedule)"
              without-hint
              clearable
            />
          </td>
          <td v-if="showDestination">
            <div class="caption" v-if="entry.schedule.direct_sale && entry.schedule.direct_sale.sales_order">
              {{ entry.schedule.direct_sale.sales_order.code }}
            </div>
            <v-autocomplete
              v-else
              v-model="entry.schedule.destination_warehouse_id"
              :items="physicalWarehouses"
              item-text="label"
              item-value="id"
              :placeholder="$t('STOCK.PURCHASE_ORDERS.SCHEDULE_DESTINATION_DEFAULT')"
              solo
              dense
              hide-details
              clearable
              :disabled="!canEditSchedule(entry.schedule)"
            />
            <v-btn
              v-if="canEditSchedule(entry.schedule)"
              x-small
              text
              color="primary"
              class="mt-1"
              @click="$emit('direct-sale', entry.schedule)"
            >
              {{ $t("STOCK.PURCHASE_ORDERS.DIRECT_SALE") }}
            </v-btn>
          </td>
          <td v-if="showSource">
            <v-autocomplete
              v-model="entry.schedule.source_warehouse_id"
              :items="warehouses"
              item-text="label"
              item-value="id"
              :placeholder="$t('STOCK.SALES_ORDERS.SCHEDULE_SOURCE_DEFAULT')"
              solo
              dense
              hide-details
              clearable
              :disabled="!canEditSchedule(entry.schedule)"
            />
          </td>
          <td class="text-center">
            <v-tooltip bottom :disabled="!scheduleNote(entry.schedule)">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  :aria-label="$t('STOCK.SALES_ORDERS.SCHEDULE_NOTE')"
                  :disabled="!canEdit && !scheduleNote(entry.schedule)"
                  v-on="on"
                  @click="openNote(entry.schedule)"
                >
                  <v-icon small :color="scheduleNote(entry.schedule) ? 'primary' : 'grey'">
                    {{ scheduleNote(entry.schedule) ? "mdi-note-text" : "mdi-note-plus-outline" }}
                  </v-icon>
                </v-btn>
              </template>
              <span class="stock-schedule-note-tooltip">{{ scheduleNote(entry.schedule) }}</span>
            </v-tooltip>
          </td>
          <td>
            <div class="d-flex align-center">
              <StockOrderLineDeliveryStatus
                v-if="entry.schedule.delivery_note && entry.schedule.delivery_note.id"
                :schedules="[entry.schedule]"
              />
              <v-btn
                v-else-if="canRemoveSchedule(entry.schedule)"
                icon
                x-small
                @click="removeSchedule(entry.index)"
              >
                <v-icon small>mdi-close</v-icon>
              </v-btn>
            </div>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <v-dialog v-model="noteDialog" max-width="520">
      <v-card v-if="noteTarget">
        <v-card-title class="text-subtitle-1">{{ $t("STOCK.SALES_ORDERS.SCHEDULE_NOTE") }}</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="noteTarget.note"
            outlined
            auto-grow
            rows="4"
            counter="500"
            maxlength="500"
            :disabled="!canEdit"
            :placeholder="$t('STOCK.SALES_ORDERS.SCHEDULE_NOTE_PLACEHOLDER')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="noteDialog = false">
            {{ $t("GENERAL.LABELS.CLOSE_BUTTON") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DatePicker from "@/views/partials/form/DatePicker.vue";
import StockOrderLineDeliveryStatus from "@/views/partials/stock/StockOrderLineDeliveryStatus.vue";

export default {
  name: "StockOrderScheduleLines",
  components: { DatePicker, StockOrderLineDeliveryStatus },
  props: {
    line: { type: Object, required: true },
    quantityKey: { type: String, default: "ordered_quantity" },
    defaultEtd: { type: String, default: null },
    defaultEta: { type: String, default: null },
    canEdit: { type: Boolean, default: false },
    showDestination: { type: Boolean, default: false },
    showSource: { type: Boolean, default: false },
    warehouses: { type: Array, default: () => [] },
    onlyUnplanned: { type: Boolean, default: false },
  },
  data() {
    return {
      noteDialog: false,
      noteTarget: null,
    };
  },
  computed: {
    physicalWarehouses() {
      return (this.warehouses || []).filter((warehouse) => !warehouse.type || warehouse.type === "physical");
    },
    visibleSchedules() {
      return (this.line.schedules || [])
        .map((schedule, index) => ({ schedule, index }))
        .filter(({ schedule }) => !this.onlyUnplanned || !this.scheduleHasDeliveryNote(schedule));
    },
    allAssigned() {
      return (this.line.schedules || []).length > 0
        && (this.line.schedules || []).every((schedule) => schedule.is_assigned);
    },
    orderedQty() {
      return this.line[this.quantityKey];
    },
  },
  watch: {
    orderedQty() {
      this.ensureSchedules();
    },
  },
  created() {
    this.ensureSchedules();
  },
  methods: {
    scheduleNote(schedule) {
      return String(schedule?.note || "").trim();
    },
    openNote(schedule) {
      if (!this.canEdit && !this.scheduleNote(schedule)) {
        return;
      }
      this.noteTarget = schedule;
      this.noteDialog = true;
    },
    scheduleHasDeliveryNote(schedule) {
      return Boolean(schedule.is_assigned || (schedule.delivery_note && schedule.delivery_note.id));
    },
    canEditSchedule(schedule) {
      return this.canEdit && !schedule.is_assigned;
    },
    freeSchedules() {
      return (this.line.schedules || []).filter((schedule) => !schedule.is_assigned);
    },
    canRemoveSchedule(schedule) {
      return this.canEdit && !schedule.is_assigned && this.freeSchedules().length > 1;
    },
    removeSchedule(index) {
      const schedules = this.line.schedules || [];
      const removed = schedules[index];
      if (!removed || !this.canRemoveSchedule(removed)) {
        return;
      }
      const target = schedules.find((schedule, i) => i !== index && !schedule.is_assigned);
      if (!target) {
        return;
      }
      const qty = Number(removed.quantity || 0);
      schedules.splice(index, 1);
      if (qty > 0) {
        target.quantity = Number((Number(target.quantity || 0) + qty).toFixed(4));
      }
    },
    ensureSchedules() {
      if (!Array.isArray(this.line.schedules)) {
        this.$set(this.line, "schedules", []);
      }
      const qty = Number(this.line[this.quantityKey] || 0);
      if (!this.line.schedules.length) {
        this.line.schedules.push({
          quantity: qty,
          etd_date: this.defaultEtd,
          eta_date: this.defaultEta,
          source_warehouse_id: null,
          note: null,
          is_assigned: false,
        });
        return;
      }
      const free = this.line.schedules.filter((schedule) => !schedule.is_assigned);
      if (free.length === 1 && this.line.schedules.length === 1) {
        free[0].quantity = qty;
      }
    },
    addSchedule() {
      this.ensureSchedules();
      const last = this.line.schedules[this.line.schedules.length - 1];
      const splitQty = Math.max(0, Number(last.quantity || 0) / 2);
      if (!last.is_assigned && splitQty > 0) {
        last.quantity = Number((Number(last.quantity) - splitQty).toFixed(4));
      }
      this.line.schedules.push({
        quantity: Number(splitQty.toFixed(4)) || 0,
        etd_date: last?.etd_date || this.defaultEtd,
        eta_date: last?.eta_date || this.defaultEta,
        destination_warehouse_id: last?.destination_warehouse_id || null,
        source_warehouse_id: last?.source_warehouse_id || null,
        note: null,
        is_assigned: false,
      });
    },
  },
};
</script>

<style scoped>
.stock-schedule-note-tooltip {
  display: block;
  max-width: 320px;
  white-space: pre-wrap;
}
</style>
